import React, { useEffect, useMemo, useState } from "react";
import { Bookmark, BookmarkPlus, Loader2 } from "lucide-react";
import ModalOrDrawer from "ui/ModalOrDrawer";
import EmptyState from "ui/EmptyState";
import { Button } from "ui/Button";
import EntityAvatar from "components/Entities/EntityAvatar";
import CollectionItemRow from "components/Collections/CollectionItemRow";
import NewCollectionModal from "components/Collections/NewCollectionModal";
import {
  useCollectionMutation,
  useEntitySaveState,
  useToggleSavedEntityMutation,
  useUserCollections,
} from "components/Collections/useCollections";
import { toast } from "sonner";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  entityId: number;
  entityType: string;
  onSavedChange?: (action: "save" | "unsave") => void;
}

const SaveCollectionsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  entityId,
  entityType,
  onSavedChange,
}) => {
  const [showNewCollectionModal, setShowNewCollectionModal] = useState(false);
  const collectionsQuery = useUserCollections({ entityId, entityType }, isOpen);
  const saveStateQuery = useEntitySaveState(entityType, entityId, isOpen);
  const collectionMutation = useCollectionMutation(entityType, entityId);
  const saveMutation = useToggleSavedEntityMutation(entityType, entityId);

  useEffect(() => {
    if (!isOpen) {
      setShowNewCollectionModal(false);
    }
  }, [isOpen]);

  const collections = collectionsQuery.data ?? [];
  const saveState = saveStateQuery.data;
  const isBusy =
    collectionsQuery.isLoading ||
    saveStateQuery.isLoading ||
    collectionMutation.isLoading ||
    saveMutation.isLoading;

  const headerBookmarkLabel = useMemo(
    () => (saveState?.isSaved ? "Saved" : "Save to Saved"),
    [saveState?.isSaved]
  );

  const toggleSavedState = async () => {
    try {
      if (saveState?.isSaved) {
        await saveMutation.unsave();
        toast.success("Removed from Saved");
        onSavedChange?.("unsave");
        return;
      }
      await saveMutation.save();
      toast.success("Saved to Saved");
      onSavedChange?.("save");
    } catch {
      toast.error("Unable to update Saved");
    }
  };

  const handleCollectionToggle = async (collectionId: number, collectionTitle: string, containsEntity: boolean) => {
    try {
      if (containsEntity) {
        await collectionMutation.removeItem(collectionId);
        toast.success(`Removed from ${collectionTitle}`);
        return;
      }
      await collectionMutation.addItem(collectionId);
      toast.success(`Added to ${collectionTitle}`);
    } catch {
      toast.error("Unable to update collection");
    }
  };

  return (
    <>
      <ModalOrDrawer
        isOpen={isOpen}
        onClose={onClose}
        stickyFooter={false}
        className="space-y-4"
        layerBase={5200}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-2xl border border-eoe-icon-hover/60 bg-eoe-icon-hover/10 p-3 dark:border-eoe-dark-icon-hover/60 dark:bg-eoe-dark-icon-hover/20">
            <div className="min-w-0 flex-1">
              <EntityAvatar
                entityId={entityId}
                entityType={entityType as
                  | "issue"
                  | "impact"
                  | "solution"
                  | "feedback"
                  | "action"
                  | "crosspost"
                  | "comment"
                  | "teabate"}
                isInputField={false}
                showLink={false}
              />
            </div>
            <button
              type="button"
              onClick={() => void toggleSavedState()}
              disabled={saveMutation.isLoading}
              className="rounded-full p-2 text-eoe-text-heading transition-colors hover:bg-eoe-icon-hover dark:text-eoe-dark-text-heading dark:hover:bg-eoe-dark-icon-hover disabled:opacity-50"
              aria-label={headerBookmarkLabel}
              title={headerBookmarkLabel}
            >
              {saveMutation.isLoading ? (
                <Loader2 className="size-5 animate-spin" aria-hidden />
              ) : (
                <Bookmark
                  className={`size-6 ${saveState?.isSaved ? "fill-current" : "fill-transparent"}`}
                  aria-hidden
                />
              )}
            </button>
          </div>

          <div className="px-1 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">Private</div>

          <div className="border-t border-eoe-icon-hover/60 pt-4 dark:border-eoe-dark-icon-hover/60">
            {collectionsQuery.isError ? (
              <div className="rounded-2xl border border-warning-red/30 bg-warning-red/10 px-4 py-3 text-sm text-warning-red dark:border-warning-red-dark/30 dark:bg-warning-red-dark/10 dark:text-warning-red-dark">
                Collections could not be loaded right now.
              </div>
            ) : null}

            {!collectionsQuery.isError && collectionsQuery.isLoading ? (
              <div className="flex items-center justify-center py-12 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                Loading collections...
              </div>
            ) : null}

            {!collectionsQuery.isError && !collectionsQuery.isLoading && collections.length === 0 ? (
              <div className="space-y-6 py-6">
                <EmptyState
                  icon={<BookmarkPlus aria-hidden />}
                  title="Collect the posts you love"
                  subtitle="Save posts in collections just for you or build a collection with others."
                />
                <div className="px-2">
                  <Button
                    type="button"
                    className="w-full rounded-full"
                    onClick={() => setShowNewCollectionModal(true)}
                  >
                    Start a collection
                  </Button>
                </div>
              </div>
            ) : null}

            {!collectionsQuery.isError && !collectionsQuery.isLoading && collections.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between px-1">
                  <div className="text-xl font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
                    Collections
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    className="px-0 text-base font-semibold text-primary-action-text hover:bg-transparent hover:text-primary-action-text-hover"
                    onClick={() => setShowNewCollectionModal(true)}
                  >
                    New collection
                  </Button>
                </div>

                <div className="space-y-1">
                  {collections.map((collection) => (
                    <CollectionItemRow
                      key={collection.id}
                      collection={collection}
                      disabled={isBusy}
                      onToggle={() =>
                        void handleCollectionToggle(
                          collection.id,
                          collection.title || "collection",
                          collection.contains_entity
                        )
                      }
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </ModalOrDrawer>

      <NewCollectionModal
        isOpen={showNewCollectionModal}
        onClose={() => setShowNewCollectionModal(false)}
        entityId={entityId}
        entityType={entityType}
        layerBase={5400}
        onCreated={() => setShowNewCollectionModal(false)}
      />
    </>
  );
};

export default SaveCollectionsModal;
